import { AI_ANALYSIS_ACTION_PERMISSIONS, authorizeAiAnalysisAction, requireAiAnalysisRun, mayReplaceReviewedFindings } from '../utils/aiAnalysisAuthorization';
// AI_ANALYSIS: jobs, polling, findings and decisions for the Analysis "AI Analysis" tab (AVA-531).
// All non-trivial logic lives in the node-tested helpers (findingsMetrics, jobTree); this module only wires state.
import Vue from 'vue';
import analysisJobService from '../service/analysisJob.service';
import aIInspectService from '../service/aIInspect.service';
import { APPROVAL, API_JOB_STATUS, isTerminalStatus, badgeOrder, computeKpis, reviewStatus, normalizeFinding } from '../utils/findingsMetrics';
import { componentPatch } from '../utils/findingComponents';
import { pendingFindings } from '../utils/reportFormat';
import { JOB_TYPES, buildJobTree, splitImagesIds, mergeImages, normalizeJob } from '../utils/jobTree';
import { log, error } from '@/utils/debugLogger';

export const POLL_INTERVAL_MS = 15000;
export const POLL_MAX_INTERVAL_MS = 60000;
export const FINDINGS_PAGE_SIZE = 1000;
// Same defaults AI_INSPECT/GET_IMAGES applies when an image has no stored size.
const DEFAULT_IMAGE_WIDTH = 4057;
const DEFAULT_IMAGE_HEIGHT = 2028;
export const RUN_BLOCKED_MESSAGE = 'An analysis is already running for this facility. Wait for it to finish before starting another.';
const NO_SNAPS_MESSAGE = 'This AVA Inspect has no snaps yet. Add a snap on a 360 scan first.';
/** Snap lists fetched at once when the Run dialog opens (one request per AVA Inspect tag). */
const SNAP_LOAD_CONCURRENCY = 6;
const COMPLETED_MESSAGE = 'AI analysis completed';

const initialState = () => ({
  facilityId: null,
  scopeVersion: 0,
  jobsRevision: 0,
  processingCountRequest: 0,
  jobs: [],
  jobsLoading: false,
  findingsByJobId: {},
  findingsLoadingJobId: null,
  snapsByAvaInspectId: {},
  facilityProcessingCount: 0,
  pollTimer: null,
  pollIntervalMs: POLL_INTERVAL_MS,
  lastError: null,
});

const toArray = (data) => (Array.isArray(data) ? data : data ? [data] : []);
const newestFirst = (a, b) => String(b.createDate || '').localeCompare(String(a.createDate || ''));

/** vuetify-dialog installs `$dialog` on the prototype; guard so a missing plugin never breaks polling. */
function notifySuccess(message) {
  const dialog = Vue.prototype.$dialog;
  if (dialog && dialog.notify && typeof dialog.notify.success === 'function') dialog.notify.success(message);
}

export const AI_ANALYSIS = {
  namespaced: true,
  state: initialState(),
  getters: {
    JOBS: (s) => s.jobs,
    JOBS_LOADING: (s) => s.jobsLoading,
    LAST_ERROR: (s) => s.lastError,
    JOB_BY_ID: (s) => (id) => s.jobs.find((j) => j.id === id) || null,
    JOBS_BY_IMAGE_ID: (s) =>
      s.jobs.reduce((acc, job) => {
        splitImagesIds(job.imagesIds).forEach((imageId) => {
          acc[imageId] = [...(acc[imageId] || []), job];
        });
        return acc;
      }, {}),
    LATEST_JOB_FOR_IMAGE: (s, g) => (imageId) => [...(g.JOBS_BY_IMAGE_ID[imageId] || [])].sort(newestFirst)[0] || null,
    HAS_RUNNING_JOBS: (s) => s.jobs.some((j) => !isTerminalStatus(j.status)),
    FACILITY_PROCESSING_COUNT: (s) => s.facilityProcessingCount,
    RUN_BLOCKED_REASON: (s, g) => (g.HAS_RUNNING_JOBS || s.facilityProcessingCount > 0 ? RUN_BLOCKED_MESSAGE : null),
    // Two stores carry AVA Inspect tags: the facility load fills TAGS.avaInspectTags (root FETCH_AVA_INSPECT_TAGS),
    // the tags register fills the namespaced AVA_INSPECT module. Merge both, keyed by id.
    AVA_INSPECTS_BY_ID: (s, g, rootState, rootGetters) =>
      Object.fromEntries(
        [...(rootGetters['AVA_INSPECT/GET_AVA_INSPECT_TAGS'] || []), ...((rootState.TAGS && rootState.TAGS.avaInspectTags) || [])]
          .filter((t) => t && t.id)
          .map((t) => [t.id, t]),
      ),
    // Root `getImages` is empty on the Analysis route (only Admin -> Projects fills state.projects);
    // AI Inspect's image list is the live source there, so merge both (AI Inspect first).
    IMAGES_BY_ID: (s, g, rootState, rootGetters) =>
      Object.fromEntries(mergeImages(rootGetters['AI_INSPECT/IMAGES'], rootGetters.getImages).map((i) => [i.id, i])),
    JOB_TREE: (s, g) => buildJobTree(s.jobs, { avaInspectsById: g.AVA_INSPECTS_BY_ID, imagesById: g.IMAGES_BY_ID }),
    FINDINGS_FOR_JOB: (s) => (jobId) => badgeOrder(s.findingsByJobId[jobId] || []),
    FINDINGS_LOADING_JOB_ID: (s) => s.findingsLoadingJobId,
    // Rejected areas leave the report (the API soft-deletes them), so they do not count towards the KPIs.
    KPIS_FOR_JOB: (s, g) => (jobId) =>
      computeKpis(
        (s.findingsByJobId[jobId] || []).filter((f) => f.approvalStatus !== APPROVAL.REJECTED),
        Object.fromEntries(
          Object.values(g.IMAGES_BY_ID).map((i) => [i.id, { width: i.width || DEFAULT_IMAGE_WIDTH, height: i.height || DEFAULT_IMAGE_HEIGHT }]),
        ),
      ),
    REVIEW_STATUS_FOR_JOB: (s) => (jobId) => reviewStatus(s.findingsByJobId[jobId] || []),
    SNAPS_FOR: (s) => (avaInspectId) => s.snapsByAvaInspectId[avaInspectId] || [],
    AVA_INSPECT_OPTIONS: (s, g) =>
      Object.values(g.AVA_INSPECTS_BY_ID).map((t) => ({
        id: t.id,
        title: t.title || t.name || t.id,
        masterTagName: (t.masterTag && t.masterTag.name) || '',
      })),
  },
  mutations: {
    SET_FACILITY_ID(state, facilityId) { state.facilityId = facilityId; },
    // Live jobs arrive without jobType and with UTC dates lacking a designator; normalise once here.
    SET_JOBS(state, jobs) {
      const cancelledIds = new Set(state.jobs.filter((job) => job.status === API_JOB_STATUS.CANCELLED).map((job) => job.id));
      state.jobs = jobs.map((job) => normalizeJob(cancelledIds.has(job.id) ? { ...job, status: API_JOB_STATUS.CANCELLED } : job));
    },
    MARK_JOB_CANCELLED(state, id) {
      state.jobs = state.jobs.map((job) => job.id === id ? { ...job, status: API_JOB_STATUS.CANCELLED } : job);
      state.jobsRevision++;
    },
    BEGIN_PROCESSING_COUNT(state) { state.processingCountRequest++; },
    UPSERT_JOBS(state, rawJobs) {
      const jobs = rawJobs.map(normalizeJob);
      const incoming = new Map(jobs.map((j) => [j.id, j]));
      state.jobs = [
        ...state.jobs.map((j) => incoming.get(j.id) || j),
        ...jobs.filter((j) => !state.jobs.some((existing) => existing.id === j.id)),
      ];
    },
    SET_JOBS_LOADING(state, value) { state.jobsLoading = value; },
    SET_FINDINGS(state, { jobId, items }) { Vue.set(state.findingsByJobId, jobId, items.map(normalizeFinding)); },
    SET_FINDINGS_LOADING_JOB_ID(state, jobId) { state.findingsLoadingJobId = jobId; },
    PATCH_FINDING(state, { jobId, id, changes }) {
      Vue.set(state.findingsByJobId, jobId, (state.findingsByJobId[jobId] || []).map((f) => (f.id === id ? normalizeFinding({ ...f, ...changes }) : f)));
    },
    SET_SNAPS(state, { avaInspectId, snaps }) { Vue.set(state.snapsByAvaInspectId, avaInspectId, [...snaps]); },
    SET_FACILITY_PROCESSING_COUNT(state, count) { state.facilityProcessingCount = Number(count) || 0; },
    SET_POLL_TIMER(state, timer) { state.pollTimer = timer; },
    SET_POLL_INTERVAL(state, ms) { state.pollIntervalMs = ms; },
    SET_LAST_ERROR(state, err) { state.lastError = err ? String(err.message || err) : null; },
    RESET(state) { Object.assign(state, initialState(), { scopeVersion: state.scopeVersion + 1 }); },
  },
  actions: {
    async LOAD_JOBS({ commit, dispatch, state }, facilityId) {
      if (state.facilityId && state.facilityId !== facilityId) {
        dispatch('STOP_POLLING');
        commit('RESET');
      }
      commit('SET_FACILITY_ID', facilityId);
      const scope = state.scopeVersion;
      const revision = state.jobsRevision;
      const isCurrent = () => state.facilityId === facilityId && state.scopeVersion === scope && state.jobsRevision === revision;
      let loaded = false;
      commit('SET_JOBS_LOADING', true);
      try {
        const { data } = await analysisJobService.listJobs(facilityId);
        if (!isCurrent()) return false;
        commit('SET_JOBS', (data && data.items) || []);
        commit('SET_LAST_ERROR', null);
        loaded = true;
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        if (!isCurrent()) return false;
        error('[AI_ANALYSIS] LOAD_JOBS failed', err);
        commit('SET_LAST_ERROR', err);
      } finally {
        if (isCurrent()) commit('SET_JOBS_LOADING', false);
      }
      if (!isCurrent()) return false;
      const countLoaded = await dispatch('REFRESH_PROCESSING_COUNT');
      if (!isCurrent()) return false;
      dispatch('ENSURE_POLLING');
      return loaded && countLoaded;
    },
    async START_FULL_SCAN({ state, commit, dispatch }, { imagesIds }) {
      const { data } = await analysisJobService.startAnalysisJob({ facilityId: state.facilityId, imagesIds, jobType: JOB_TYPES.FULL_SCAN });
      const created = toArray(data); // live API returns one VM, spec contract returns an array
      commit('UPSERT_JOBS', created);
      commit('SET_POLL_INTERVAL', POLL_INTERVAL_MS);
      dispatch('ENSURE_POLLING');
      return created;
    },
    async LOAD_SNAPS({ state, commit }, avaInspectId) {
      try {
        const { data } = await analysisJobService.getSnaps(state.facilityId, avaInspectId);
        commit('SET_SNAPS', { avaInspectId, snaps: toArray(data) });
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        log('[AI_ANALYSIS] snaps of an AVA Inspect tag could not be loaded', err);
        commit('SET_SNAPS', { avaInspectId, snaps: [] });
      }
    },
    /** Snap lists of every AVA Inspect tag, for the Run dialog's counts; a few requests at a time. */
    async LOAD_ALL_SNAPS({ getters, dispatch }) {
      const queue = Object.keys(getters.AVA_INSPECTS_BY_ID);
      const worker = async () => {
        while (queue.length) await dispatch('LOAD_SNAPS', queue.shift());
      };
      await Promise.all(Array.from({ length: Math.min(SNAP_LOAD_CONCURRENCY, queue.length) }, worker));
    },
    async START_AVA_INSPECT_RUN({ state, commit, dispatch, rootGetters }, { avaInspectId }) {
      await dispatch('LOAD_SNAPS', avaInspectId);
      const snaps = state.snapsByAvaInspectId[avaInspectId] || [];
      if (!snaps.length) throw new Error(NO_SNAPS_MESSAGE);
      // The API rounds the snaps (up to 4 per model request) and crops each stored outline itself.
      const payload = {
        facilityId: state.facilityId,
        avaInspectId,
        jobType: JOB_TYPES.AVA_INSPECT,
        snapIds: snaps.map((s) => s.id),
        clearPreviousFindings: mayReplaceReviewedFindings(rootGetters),
      };
      requireAiAnalysisRun(rootGetters);
      const { data } = await analysisJobService.startAnalysisJob(payload);
      const created = toArray(data);
      commit('UPSERT_JOBS', created);
      commit('SET_POLL_INTERVAL', POLL_INTERVAL_MS);
      dispatch('ENSURE_POLLING');
      return created;
    },
    ENSURE_POLLING({ state, getters, commit, dispatch }) {
      if (state.pollTimer || !getters.HAS_RUNNING_JOBS) return;
      const timer = setTimeout(() => dispatch('POLL_JOBS'), state.pollIntervalMs);
      commit('SET_POLL_TIMER', timer);
    },
    async POLL_JOBS({ state, getters, commit, dispatch }) {
      commit('SET_POLL_TIMER', null);
      if (!state.facilityId) return;
      const facilityId = state.facilityId;
      const scope = state.scopeVersion;
      const revision = state.jobsRevision;
      const isCurrent = () => state.facilityId === facilityId && state.scopeVersion === scope && state.jobsRevision === revision;
      const before = new Map(state.jobs.map((j) => [j.id, j.status]));
      try {
        const { data } = await analysisJobService.listJobs(facilityId);
        if (!isCurrent()) return;
        const jobs = (data && data.items) || [];
        commit('SET_JOBS', jobs);
        const justCompleted = jobs.filter(
          (j) => j.status === API_JOB_STATUS.COMPLETED && before.get(j.id) && before.get(j.id) !== API_JOB_STATUS.COMPLETED,
        );
        if (justCompleted.length) notifySuccess(COMPLETED_MESSAGE);
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        error('[AI_ANALYSIS] POLL_JOBS failed', err);
      }
      if (!isCurrent()) return;
      dispatch('REFRESH_PROCESSING_COUNT');
      if (getters.HAS_RUNNING_JOBS) {
        commit('SET_POLL_INTERVAL', Math.min(state.pollIntervalMs * 2, POLL_MAX_INTERVAL_MS));
        dispatch('ENSURE_POLLING');
      } else {
        commit('SET_POLL_INTERVAL', POLL_INTERVAL_MS);
      }
    },
    STOP_POLLING({ state, commit }) {
      if (state.pollTimer) clearTimeout(state.pollTimer);
      commit('SET_POLL_TIMER', null);
    },
    async REFRESH_PROCESSING_COUNT({ state, commit }) {
      if (!state.facilityId) return false;
      const facilityId = state.facilityId;
      const scope = state.scopeVersion;
      commit('BEGIN_PROCESSING_COUNT');
      const request = state.processingCountRequest;
      try {
        const { data } = await aIInspectService.getProcessingCount(facilityId);
        if (state.facilityId !== facilityId || state.scopeVersion !== scope || state.processingCountRequest !== request) return false;
        commit('SET_FACILITY_PROCESSING_COUNT', data);
        return true;
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        log('[AI_ANALYSIS] processingCount failed', err);
        return false;
      }
    },
    async LOAD_FINDINGS({ state, getters, commit }, jobId) {
      const job = getters.JOB_BY_ID(jobId);
      if (!job) return;
      commit('SET_FINDINGS_LOADING_JOB_ID', jobId);
      try {
        const scanNames = splitImagesIds(job.imagesIds).map((id) => (getters.IMAGES_BY_ID[id] || {}).name).filter(Boolean);
        // analysisJobId is honoured by the mock; the live API ignores it (API ask) so filters.scan
        // is sent as well. On live, that includes older findings for the same scan until the ask lands.
        const { data } = await aIInspectService.get({
          facilityId: state.facilityId,
          analysisJobId: jobId,
          page: 1,
          itemsPerPage: FINDINGS_PAGE_SIZE,
          sortBy: 'CreateDate',
          sortDesc: false,
          filters: { scan: scanNames },
        });
        commit('SET_FINDINGS', { jobId, items: (data && data.items) || [] });
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        error('[AI_ANALYSIS] LOAD_FINDINGS failed', err);
        commit('SET_LAST_ERROR', err);
      } finally {
        commit('SET_FINDINGS_LOADING_JOB_ID', null);
      }
    },
    async ACCEPT({ commit }, { jobId, id }) {
      await aIInspectService.accept(id);
      commit('PATCH_FINDING', { jobId, id, changes: { approvalStatus: APPROVAL.ACCEPTED, approvalStatusChangedOn: new Date().toISOString() } });
    },
    async REJECT({ commit }, { jobId, id }) {
      // Reject/{id} soft-deletes on the API; the row stays listed as Rejected for this session (approved showcase).
      await aIInspectService.reject(id);
      commit('PATCH_FINDING', { jobId, id, changes: { approvalStatus: APPROVAL.REJECTED, approvalStatusChangedOn: new Date().toISOString() } });
    },
    async ACCEPT_ALL({ state, commit }, jobId) {
      const pending = pendingFindings(state.findingsByJobId[jobId]);
      if (!pending.length) return 0;
      // updateMultiple copies every listed field from the payload: send the FULL finding, never {id, approvalStatus}.
      await aIInspectService.updateMultiple(pending.map((f) => ({ ...f, approvalStatus: APPROVAL.ACCEPTED })));
      const changedOn = new Date().toISOString();
      pending.forEach((f) =>
        commit('PATCH_FINDING', { jobId, id: f.id, changes: { approvalStatus: APPROVAL.ACCEPTED, approvalStatusChangedOn: changedOn } }),
      );
      return pending.length;
    },
    /**
     * Links several findings to one component, or clears the link when `component` is null. One updateMultiple call
     * for the whole selection; the single-row, panel and bulk paths all come through here.
     */
    async LINK_COMPONENT({ state, commit }, { jobId, ids, component }) {
      const wanted = new Set(ids || []);
      const targets = (state.findingsByJobId[jobId] || []).filter((f) => wanted.has(f.id));
      if (!targets.length) return 0;
      const changes = componentPatch(component);
      // updateMultiple copies every listed field from the payload: send the FULL finding plus the new link.
      await aIInspectService.updateMultiple(targets.map((f) => ({ ...f, ...changes })));
      targets.forEach((f) => commit('PATCH_FINDING', { jobId, id: f.id, changes }));
      return targets.length;
    },
    async REJECT_ALL({ state, dispatch }, jobId) {
      const pending = pendingFindings(state.findingsByJobId[jobId]);
      // Reject/{id} is the only endpoint that soft-deletes; loop it (API ask 6 for a bulk variant).
      for (const f of pending) {
        await dispatch('REJECT', { jobId, id: f.id });
      }
      return pending.length;
    },
    async CANCEL_JOB({ state, commit, dispatch }, { id, facilityId }) {
      const scope = state.scopeVersion;
      const isCurrent = () => state.facilityId === facilityId && state.scopeVersion === scope;
      if (!isCurrent()) return { cancelled: false, contextChanged: true };
      const response = await analysisJobService.cancelJob(id);
      if (response.status !== 200 || !response.data || response.data.jobId !== id || response.data.status !== API_JOB_STATUS.CANCELLED) {
        throw new Error('Cancellation was not confirmed');
      }
      if (!isCurrent()) return { cancelled: true, contextChanged: true };
      commit('MARK_JOB_CANCELLED', id);
      dispatch('STOP_POLLING');
      // Cancellation has already succeeded. A later refresh failure must never report the request as failed.
      let refreshed = false;
      try {
        refreshed = await dispatch('LOAD_JOBS', facilityId);
      } catch (err) {
        if (err && err.response && [401, 403].includes(err.response.status)) throw err;
        log('[AI_ANALYSIS] cancellation refresh failed', err);
      }
      return { cancelled: true, refreshFailed: !refreshed };
    },
    RESET({ dispatch, commit }) {
      dispatch('STOP_POLLING');
      commit('RESET');
    },
  },
};

for (const [name, capability] of Object.entries(AI_ANALYSIS_ACTION_PERMISSIONS)) {
  AI_ANALYSIS.actions[name] = authorizeAiAnalysisAction(AI_ANALYSIS.actions[name], capability);
}
